import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './RiskMatrixModal.css';

const ZONE_COLORS = {
  'Low': '#d4edda',
  'Medium': '#fff3cd',
  'High': '#ffe5cc',
  'Very High': '#f8d7da'
};

const getZoneColor = (zoneName) => ZONE_COLORS[zoneName] || '#e9ecef';

/**
 * Read-only view of the configured risk matrix, mirroring the admin
 * Risk Matrix settings tab (admin-settings?tab=risk-matrix).
 */
const RiskMatrixModal = ({ show, onClose, riskMatrixConfig, customFields, currentScore }) => {
  const matrixValues = riskMatrixConfig?.matrix_values || [];
  const zoneThresholds = riskMatrixConfig?.zone_thresholds || {};
  const zoneNames = riskMatrixConfig?.zone_names || Object.keys(zoneThresholds);

  // Mirror the admin editor's dynamic X-axis label based on threat score configuration
  const threatAxisLabel = useMemo(() => {
    const fieldIds = riskMatrixConfig?.threat_score_fields || [];
    const labelFor = (id) => {
      const field = (customFields || []).find(cf => cf.id === id);
      return field ? (field.label || field.name) : null;
    };

    if (fieldIds.length === 1) {
      return labelFor(fieldIds[0]) || 'Impact Score';
    }

    if (fieldIds.length === 2) {
      const first = labelFor(fieldIds[0]);
      const second = labelFor(fieldIds[1]);
      if (first && second) {
        return riskMatrixConfig?.threat_score_calculation === 'max'
          ? `Max(${first}, ${second})`
          : first;
      }
    }

    return 'Impact Score';
  }, [riskMatrixConfig, customFields]);

  const getCellZone = (value) => {
    for (const [zoneName, threshold] of Object.entries(zoneThresholds)) {
      if (threshold && value >= threshold.min && value <= threshold.max) {
        return zoneName;
      }
    }
    return null;
  };

  const highlightScore = currentScore !== null && currentScore !== undefined && !isNaN(currentScore)
    ? parseInt(currentScore)
    : null;

  if (!show) return null;

  return createPortal(
    <div className="risk-matrix-modal-overlay" onClick={onClose}>
      <div className="risk-matrix-modal" onClick={e => e.stopPropagation()}>
        <div className="risk-matrix-modal-header">
          <div>
            <h3>Risk Matrix</h3>
            {highlightScore !== null && (
              <p className="risk-matrix-modal-subtitle">
                Current risk score: <strong>{highlightScore}</strong>
                {getCellZone(highlightScore) ? ` (${getCellZone(highlightScore)})` : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            className="risk-matrix-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="risk-matrix-modal-body">
          {matrixValues.length > 0 ? (
            <>
              <div className="risk-matrix-view-container">
                <div className="risk-matrix-view-y-label">
                  <span>Probability</span>
                </div>

                <div className="risk-matrix-view-table-wrapper">
                  <table className="risk-matrix-view-table">
                    <tbody>
                      {/* Reverse the rows so (1,1) appears at bottom left */}
                      {[...matrixValues].reverse().map((row, displayRowIndex) => {
                        const actualRowIndex = matrixValues.length - 1 - displayRowIndex;
                        return (
                          <tr key={actualRowIndex}>
                            <th className="risk-matrix-view-row-header">{actualRowIndex + 1}</th>
                            {row.map((cellValue, colIndex) => {
                              const zone = getCellZone(cellValue);
                              const isCurrent = highlightScore !== null && cellValue === highlightScore;
                              return (
                                <td key={colIndex}>
                                  <div
                                    className={`risk-matrix-view-cell ${isCurrent ? 'current' : ''}`}
                                    style={{ backgroundColor: getZoneColor(zone) }}
                                    title={zone ? `${zone} (Score: ${cellValue})` : `Score: ${cellValue}`}
                                  >
                                    {cellValue}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th className="risk-matrix-view-corner"></th>
                        {(matrixValues[0] || []).map((_, i) => (
                          <th key={i} className="risk-matrix-view-col-header">{i + 1}</th>
                        ))}
                      </tr>
                    </tfoot>
                  </table>

                  <div className="risk-matrix-view-x-label">
                    <span>{threatAxisLabel}</span>
                  </div>
                </div>
              </div>

              <div className="risk-matrix-view-legend">
                {zoneNames.map((zoneName) => {
                  const threshold = zoneThresholds[zoneName];
                  if (!threshold) return null;
                  return (
                    <div key={zoneName} className="risk-matrix-view-legend-item">
                      <span
                        className="risk-matrix-view-legend-swatch"
                        style={{ backgroundColor: getZoneColor(zoneName) }}
                      ></span>
                      <span className="risk-matrix-view-legend-label">{zoneName}</span>
                      <span className="risk-matrix-view-legend-range">
                        {threshold.min} - {threshold.max}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="risk-matrix-view-empty">
              <i className="bi bi-exclamation-circle"></i>
              <p>Risk matrix is not configured.</p>
            </div>
          )}

          <div className="risk-matrix-view-note">
            <i className="bi bi-info-circle"></i>
            <div>
              The risk zone is calculated from Probability and {threatAxisLabel}. The matrix can be
              configured in Admin Settings under Risk Matrix. Contact your system administrator to
              modify these values.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

RiskMatrixModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  riskMatrixConfig: PropTypes.shape({
    matrix_values: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
    zone_thresholds: PropTypes.object,
    zone_names: PropTypes.arrayOf(PropTypes.string),
    threat_score_fields: PropTypes.arrayOf(PropTypes.number),
    threat_score_calculation: PropTypes.string
  }),
  customFields: PropTypes.array,
  currentScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

RiskMatrixModal.defaultProps = {
  show: false,
  riskMatrixConfig: null,
  customFields: [],
  currentScore: null
};

export default RiskMatrixModal;
